import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {BokkzIbDataService, IncomeTaxEntity, Onderneming} from '@snelstart-ventures/fiscaal/domain-logic';
import {filter, Observable, shareReplay, take, tap} from 'rxjs';
import {FormControl, FormGroup} from '@angular/forms';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule} from '@angular/common';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-for',
  templateUrl: './spec-for.component.html',
  imports: [
    SharedUiFormControlsModule,
    SharedUiAlertModule,
    SharedUiDialogModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule
  ],
  styleUrls: ['./spec-for.component.scss']
})
export class SpecForComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  public dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public indexOnderneming = 0;
  public dialogVisible = false;
  public isPartner = false;

  public formGroup = new FormGroup({
    waardeOpening: new FormControl<number | null>(null),
    waardeSlot: new FormControl<number | null>(null),
    dotatieFOR: new FormControl<number | null>(0),
    vrijvalFOR: new FormControl<number | null>(null),
  })

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly indexOnderneming$: Observable<number> =
    this.dataService.indexOnderneming$.pipe(
      tap(data => {
        this.indexOnderneming = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.indexOnderneming$.subscribe();
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
    });
  }

  ngOnDestroy(): void {
    // rely on saveFrom in component
  }

  showDialog() {
    this.handleSwitch(this.dataService.incomeTaxEntity.value, this.isPartner);
    this.dialogVisible = true;
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    this.resetFormGroup();
    const ondernemingen: Onderneming[] = details.box1?.winstUitOnderneming?.ondernemingen || [];
    this.patchFormGroups(ondernemingen);
  }

  private resetFormGroup() {
    this.formGroup.reset({}, {emitEvent: false})
  }

  private patchFormGroups(ondernemingen: Onderneming[]) {
    this.formGroup.reset({}, {emitEvent: false});
    const onderneming = ondernemingen[this.indexOnderneming];
    if (!onderneming) return;
    this.formGroup.patchValue({dotatieFOR: onderneming.dotatieFOR}, {emitEvent: false});
    this.formGroup.patchValue({vrijvalFOR: onderneming.vrijvalFOR}, {emitEvent: false});
    if (onderneming.eigenVermogen?.at(3)) {
      this.formGroup.patchValue({waardeOpening : onderneming.eigenVermogen[3].waardeOpening}, {emitEvent: false});
      this.formGroup.patchValue({waardeSlot : onderneming.eigenVermogen[3].waardeSlot}, {emitEvent: false});
    }
  }

  get totaalVrijval(): any {
    const opening = this.formGroup.get('waardeOpening')?.value ?? 0;
    const dotatie = this.formGroup.get('dotatieFOR')?.value ?? 0;
    const slot = this.formGroup.get('waardeSlot')?.value ?? 0;
    const vrijval = (opening + dotatie - slot);
    this.formGroup.patchValue({vrijvalFOR: vrijval}, {emitEvent: false});
    return vrijval;
  }

  saveForm(): void {
    const idx = this.indexOnderneming;
    const formValue = this.formGroup.getRawValue();

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      const update = (aangifte: any) => {
        const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
        const onderneming = existingOndernemingen[idx] || {};

        // Clone or create the array
        const egalisatiereserve = Array.isArray(onderneming.eigenVermogen)
          ? [...onderneming.eigenVermogen]
          : [];
        // Ensure the array has at least 2 items
        while (egalisatiereserve.length < 3) {
          egalisatiereserve.push({});
        }
        // Update the third item
        egalisatiereserve[2] = formValue;

        const updatedOnderneming: Partial<Onderneming> = {
          ...onderneming,
          dotatieFOR: (formValue.dotatieFOR ?? 0) > 1 ? formValue.dotatieFOR : undefined,
          vrijvalFOR: (formValue.vrijvalFOR ?? 0) > 1 ? formValue.vrijvalFOR : undefined,
          beginstandFOR: (formValue.waardeOpening ?? 0) > 1 ? formValue.waardeOpening : undefined,
        };
        // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
        const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
        const updatedOndernemingen = existingOndernemingen.map((o: Onderneming, i: number) => i === idx ? cleanedOnderneming : o);
        return {
          ...aangifte,
          box1: {
            ...aangifte.box1,
            winstUitOnderneming: {
              ...aangifte.box1?.winstUitOnderneming,
              ondernemingen: updatedOndernemingen,
              oudedagsreserveBeginJaar: updatedOnderneming.beginstandFOR,
              vrijvalFORIvmPremieLijfente: updatedOnderneming.vrijvalFOR
            },
          },
        };
      };
      const updatedAangifte = this.isPartner
        ? {
          ...currentEntity.aangifte,
          partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
        }
        : update(currentEntity.aangifte);
      return this.dataService.deepClean({
        ...currentEntity,
        aangifte: updatedAangifte,
      }) as IncomeTaxEntity;
    });
  }
}
